import React, { useEffect, useRef, useId } from 'react';

const SIZE_CLASS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] md:max-w-[90vw]',
};

const Dialog = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  variant = 'default',
  size = 'lg',
  showHeader = true,
  showFooter = true,
  closeOnEsc = true,
  closeOnOutside = true,
  bodyClassName = '',
  headerRight = null,
}) => {
  const labelId = useId();
  const modalRef = useRef();

  // ESC to close
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handleEsc = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  // Lock background scroll
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = prevOverflow);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? labelId : undefined}
    >
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeOnOutside ? onClose : undefined}
      />

      {/* modal */}
      <div
        ref={modalRef}
        className={`relative z-10 w-full ${SIZE_CLASS[size]} rounded-2xl bg-white shadow-xl overflow-hidden`}
      >
        <div className="grid max-h-[90vh] grid-rows-[auto,1fr,auto]">
          {variant === 'default' && showHeader && (
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 id={labelId} className="text-lg font-semibold text-gray-800">
                {title}
              </h3>
              <div className="flex items-center gap-2">
                {headerRight}
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
          )}

          {variant === 'custom' && (
            <button
              onClick={onClose}
              className="absolute right-3 top-3 z-20 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close"
            >
              &times;
            </button>
          )}

          <div className={`overflow-y-auto ${bodyClassName}`}>{children}</div>

          {variant === 'default' && showFooter && (
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dialog;
